import { useCallback, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CircularProgress, IconButton } from '@mui/material';

import { type ArchiveSubject } from './ArchiveDialog';
import { WorkMenu } from './WorkMenu';

export interface QueueRowMenuProps {
  subject: ArchiveSubject;
  /**
   * The row carries a non-active disposition and is only on screen because the
   * caller asked to see hidden rows. Packet E derives this from
   * `work_disposition`.
   */
  hidden?: boolean;
  /** A verb is in flight: the kebab spins and the items refuse a second click. */
  busy?: boolean;
  /** Frozen clock for stories and tests. */
  today?: Date;
  onOpenInWorkbench: () => void;
  onArchive: (opts: { note?: string }) => void;
  onSnooze: (opts: { untilDate: string }) => void;
  onAddNote: (opts: { body: string }) => void;
  onRestore?: () => void;
}

/**
 * The row kebab for every queue surface (GDN-3380).
 *
 * ONE COMPONENT FOR THREE SURFACES. Home, Claims and any later queue get the
 * same five verbs in the same order with the same copy, because the biller's
 * question ("what can I do with this row?") does not change when they change
 * tabs, and three near-identical menus is how the answer starts to.
 *
 * Everything below the button is `WorkMenu`, which the workbench kebab renders
 * too. What is here is the trigger: a row's kebab has its own size, padding
 * and accessible name, and the busy spinner belongs to the row.
 */
export function QueueRowMenu({
  subject,
  hidden,
  busy,
  today,
  onOpenInWorkbench,
  onArchive,
  onSnooze,
  onAddNote,
  onRestore,
}: QueueRowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    /* The row underneath opens the claim on any click in its area; the kebab
       is inside it, so the trigger guards itself. Everything the menu renders
       is guarded at the overlay root instead — see `portalGuard.ts`. */
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <IconButton
        aria-label={`Actions for ${subject.patientName}`}
        onClick={openMenu}
        size="small"
        sx={{ p: 0.5 }}
      >
        {busy ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 18 }} />}
      </IconButton>
      <WorkMenu
        anchorEl={anchorEl}
        busy={busy}
        hidden={hidden}
        onAddNote={onAddNote}
        onArchive={onArchive}
        onClose={closeMenu}
        onOpenInWorkbench={onOpenInWorkbench}
        onRestore={onRestore}
        onSnooze={onSnooze}
        open={Boolean(anchorEl)}
        subject={subject}
        today={today}
      />
    </>
  );
}

export type { ArchiveSubject };
